import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api, AED_short } from '../../lib/api';
import MobileShell, { GreetingHeader } from '../../components/MobileShell';
import {
  Wallet, Building2, Sparkles, ArrowRight, TrendingUp, Bell, Trophy, Globe2,
  Gavel, Flame, MapPin, ChevronRight, Camera,
} from 'lucide-react';

/**
 * Mobile Home — INFO-FIRST dashboard (not a desktop replica).
 * Sections (top to bottom):
 *   1. Greeting + hero KPI (capital in assets)
 *   2. Iris Hot Deals — 3 top picks with images
 *   3. Yield Leaderboard — top 3 communities right now
 *   4. My Assets — active flip cards
 *   5. Market Pulse — 3 mini-stats (momentum · listings · yield)
 *   6. Alerts (latest 3)
 */

export default function MobileHome() {
  const [kpi, setKpi] = useState(null);
  const [assets, setAssets] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [radar, setRadar] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pulse, setPulse] = useState({});

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/summary').catch(() => ({ data: {} })),
      api.get('/properties').catch(() => ({ data: [] })),
      api.get('/notifications').catch(() => ({ data: [] })),
      api.get('/iris/top-picks?limit=3').catch(() => ({ data: { picks: [] } })),
      api.get('/trends/rental-leaderboard?top=3&min_listings=5').catch(() => ({ data: { leaderboard: [] } })),
    ]).then(([d, p, n, r, lb]) => {
      setKpi(d.data?.kpis);
      setAssets(p.data);
      setNotifs(n.data);
      // Normalize pre-computed top-picks to legacy radar shape
      const picks = (r.data?.picks || []).map(pk => ({
        source: pk.source,
        source_id: pk.source_id,
        hotness_score: pk.score,
        headline: pk.narrative?.headline || pk.narrative?.verdict || pk.title || pk.building,
        community: pk.community,
        building: pk.building,
        price_aed: pk.price_aed,
        gross_yield_pct: pk.signals?.gross_yield_pct,
      }));
      setRadar(picks);
      setLeaderboard(lb.data?.leaderboard || []);
      setPulse({
        activeCommunities: (lb.data?.total_communities_ranked) || 0,
        topYield: (lb.data?.leaderboard?.[0]?.gross_yield_pct) || null,
        totalListings: d.data?.kpis?.active_listings_count || null,
      });
    });
  }, []);

  const featured = assets.filter(p => p.stage === 'refurb' || p.stage === 'owned').slice(0, 4);

  return (
    <MobileShell noHeader>
      <GreetingHeader/>

      {/* Hero KPI card — capital + estimated exit */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl p-5 mb-6 bg-gradient-to-br from-[#1A2530] via-[#2a3a4a] to-[#4a6d63] text-white shadow-lg"
        data-testid="m-hero-kpi"
      >
        <div className="absolute -top-6 -right-6 opacity-15 pointer-events-none">
          <Wallet size={110} strokeWidth={1.5}/>
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-widest opacity-80 font-semibold">Portfolio value</div>
          <div className="font-serif text-4xl mt-1 mb-1">{AED_short(kpi?.capital_in_assets_aed) || '—'}</div>
          <div className="text-xs opacity-90">
            <span className="text-emerald-300">+ {AED_short(kpi?.capital_in_build_aed) || '0'}</span> in build
            {kpi?.estimated_exit_aed && (
              <> · Est. exit <span className="text-white font-medium">{AED_short(kpi.estimated_exit_aed)}</span></>
            )}
          </div>
        </div>
      </motion.div>

      {/* Iris Hot Deals — 3 top picks */}
      <section className="mb-6" data-testid="m-hot-deals">
        <SectionHeader title="🔥 Iris Hot Deals" subtitle="Ranked live" to="/app/market/live" ctaLabel="See all"/>
        {radar.length === 0 ? (
          <div className="p-4 rounded-2xl bg-white/60 border border-white/70 text-xs text-slate-500 text-center">
            Loading today&apos;s top picks…
          </div>
        ) : (
          <div className="space-y-2.5">
            {radar.map((d, i) => (
              <Link
                key={d.source_id}
                to={d.source === 'auction' ? `/m/auctions/${d.source_id}` : `/app/explain/${d.source}/${d.source_id}`}
                data-testid={`m-hot-deal-${i}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/70 border border-white/70 active:scale-98 transition-transform"
              >
                <div className={`w-14 h-14 rounded-2xl grid place-items-center text-white flex-shrink-0 ${
                  d.hotness_score >= 85 ? 'bg-gradient-to-br from-red-500 to-orange-500' :
                  d.hotness_score >= 70 ? 'bg-gradient-to-br from-amber-500 to-orange-500' :
                  'bg-gradient-to-br from-slate-500 to-slate-700'
                }`}>
                  <div className="text-center leading-none">
                    <div className="font-serif text-lg">{d.hotness_score}</div>
                    <div className="text-[7px] uppercase tracking-widest opacity-90">score</div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-medium truncate leading-tight">{d.headline || d.building}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={9}/> {d.community} · {AED_short(d.price_aed)}
                    {d.gross_yield_pct != null && d.gross_yield_pct > 0 && (
                      <span className="text-emerald-600 font-semibold">· {d.gross_yield_pct}% yield</span>
                    )}
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 flex-shrink-0"/>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Yield Leaderboard — top 3 */}
      <section className="mb-6" data-testid="m-leaderboard">
        <SectionHeader title="🏆 Top Yielding Communities" subtitle="Live rent ÷ sale" to="/app/market/leaderboard" ctaLabel="Full board"/>
        <div className="grid grid-cols-3 gap-2">
          {leaderboard.length === 0 ? [1, 2, 3].map(i => <div key={i} className="h-28 rounded-2xl bg-white/40 animate-pulse"/>) :
            leaderboard.map((r, i) => (
            <Link
              key={r.community}
              to={`/app/market/live?community=${encodeURIComponent(r.community)}&type=rent`}
              data-testid={`m-lb-${i}`}
              className={`p-3 rounded-2xl text-white flex flex-col justify-between h-28 bg-gradient-to-br ${
                i === 0 ? 'from-emerald-500 to-teal-600' :
                i === 1 ? 'from-sky-500 to-indigo-600' : 'from-purple-500 to-fuchsia-600'
              } active:scale-95 transition-transform`}
            >
              <div className="text-[9px] uppercase tracking-widest opacity-80">#{i + 1}</div>
              <div>
                <div className="font-serif text-xl leading-none">{r.gross_yield_pct}%</div>
                <div className="text-[10px] opacity-90 truncate mt-1">{r.community}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* My Assets */}
      {featured.length > 0 && (
        <section className="mb-6" data-testid="m-assets">
          <SectionHeader title="🏗️ My Assets" subtitle={`${assets.length} active`} to="/m/properties" ctaLabel="See all"/>
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2 snap-x snap-mandatory">
            {featured.map(p => (
              <Link
                key={p.id}
                to={`/m/properties/${p.id}`}
                className="flex-shrink-0 w-48 rounded-2xl bg-white/70 border border-white/70 overflow-hidden snap-start active:scale-98 transition-transform"
              >
                {p.cover_photo_url ? (
                  <img src={p.cover_photo_url} alt="" className="w-full h-24 object-cover"/>
                ) : (
                  <div className="w-full h-24 bg-gradient-to-br from-slate-200 to-slate-300 grid place-items-center">
                    <Building2 size={28} className="text-slate-400"/>
                  </div>
                )}
                <div className="p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#679286] font-semibold">{p.stage}</div>
                  <div className="text-xs font-medium truncate mt-0.5">{p.title || p.community}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{AED_short(p.asking_price_aed || p.acquisition_price_aed)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Market Pulse */}
      <section className="mb-6" data-testid="m-pulse">
        <SectionHeader title="📊 Market Pulse"/>
        <div className="grid grid-cols-3 gap-2">
          <PulseTile icon={Globe2}   label="Active" value={pulse.totalListings ? pulse.totalListings.toLocaleString() : '60K+'} sub="listings"/>
          <PulseTile icon={Trophy}   label="Best yield" value={pulse.topYield ? `${pulse.topYield}%` : '—'} sub="community"/>
          <PulseTile icon={TrendingUp} label="Ranked" value={pulse.activeCommunities || '—'} sub="communities"/>
        </div>
      </section>

      {/* Quick actions */}
      <section className="mb-6" data-testid="m-quick-actions">
        <SectionHeader title="Quick actions"/>
        <div className="grid grid-cols-4 gap-2">
          <QuickAction to="/m/deal-analyzer" icon={Sparkles} label="Analyze" color="bg-[#679286]"/>
          <QuickAction to="/app/mobile-snag" icon={Camera}   label="Snag"    color="bg-orange-500"/>
          <QuickAction to="/app/auctions"    icon={Gavel}    label="Auctions" color="bg-purple-600"/>
          <QuickAction to="/app/help"        icon={Flame}    label="Ask Iris" color="bg-[#93704A]"/>
        </div>
      </section>

      {/* Latest alerts */}
      {notifs.length > 0 && (
        <section className="mb-8" data-testid="m-alerts">
          <SectionHeader title="🔔 Latest alerts" to="/app/notifications" ctaLabel="See all"/>
          <div className="space-y-2">
            {notifs.slice(0, 3).map((n, i) => (
              <div key={n.id || i} className="p-3 rounded-2xl bg-white/60 border border-white/70 flex items-start gap-2">
                <Bell size={12} className="text-[#679286] mt-0.5 flex-shrink-0"/>
                <div className="text-xs text-slate-700 flex-1 leading-relaxed">{n.title || n.message}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </MobileShell>
  );
}

function SectionHeader({ title, subtitle, to, ctaLabel }) {
  return (
    <div className="flex items-baseline justify-between mb-3 px-1">
      <div>
        <h2 className="text-[14px] font-semibold text-slate-900">{title}</h2>
        {subtitle && <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{subtitle}</div>}
      </div>
      {to && ctaLabel && (
        <Link to={to} className="text-[11px] text-[#679286] font-medium inline-flex items-center gap-0.5">
          {ctaLabel} <ArrowRight size={11}/>
        </Link>
      )}
    </div>
  );
}

function PulseTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-3 rounded-2xl bg-white/70 border border-white/70 text-center">
      <Icon size={16} className="mx-auto text-[#679286]"/>
      <div className="font-serif text-lg mt-1">{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 leading-tight">{sub}</div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, color }) {
  return (
    <Link to={to} className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white/60 border border-white/70 active:scale-95 transition-transform">
      <div className={`w-10 h-10 rounded-2xl grid place-items-center text-white ${color}`}>
        <Icon size={18}/>
      </div>
      <span className="text-[10px] font-medium text-slate-700">{label}</span>
    </Link>
  );
}
